import railyApiService from "../raily-api.service";
import MileageAddRequest, {
  MileageAddRequestBody,
} from "./add/mileage-add.request";
import mileageAddRequestFactory from "./add/mileage-add-request.factory";
import MileageAddResponse from "./add/mileage-add.response";
import mileageListRequestFactory from "./list/mileage-list-request.factory";
import MileageListRequest from "./list/mileage-list.request";
import MileageListResponse from "./list/mileage-list.response";
import MileageDownloadRequest, {
  MileageDownloadRequestQueryParams,
} from "./download/mileage-download.request";
import mileageDownloadRequestFactory from "./download/mileage-download-request.factory";
import MileageDownloadResponse from "./download/mileage-download.response";
import MileageDetailsRequest from "./details/mileage-details.request";
import MileageDetailsResponse from "./details/mileage-details.response";
import HttpResponse from "../../../utils/http/http.response";
import MileageUpdateRequest from "./update/mileage-update-request";
import mileageUpdateRequestFactory from "./update/mileage-update-request.factory";
import mileageDownloadAllCSVRequestFactory from "./download-all-csv/mileage-download-all-csv-request.factory";
import MileageDownloadAllCSVRequest, {
  MileageDownloadAllCSVRequestQueryParams,
} from "./download-all-csv/mileage-download-all-csv.request";
import MileageDownloadAllCSVResponse from "./download-all-csv/mileage-download-all-csv.response";
import mileageSettingsAddRequestFactory from "./settings/add/mileage-settings-add-request.factory";
import MileageSettingsAddRequest, {
  MileageSettingsAddRequestBody,
} from "./settings/add/mileage-settings-add.request";
import MileageSettingsAddResponse from "./settings/add/mileage-settings-add.response";
import mileageSettingsDetailsRequestFactory from "./settings/details/mileage-settings-details-request.factory";
import MileageSettingsDetailsRequest, {
  MileageSettingsDetailsRequestQueryParams,
} from "./settings/details/mileage-settings-details.request";
import MileageSettingsDetailsResponse from "./settings/details/mileage-settings-details.response";
import mileageSettingsUpdateRequestFactory from "./settings/update/mileage-settings-update-request.factory";
import MileageSettingsUpdateRequest, {
  MileageSettingsUpdateRequestBody,
} from "./settings/update/mileage-settings-update.request";
import MileageSettingsUpdateResponse from "./settings/update/mileage-settings-update.response";
import mileageDictionaryAddRequestFactory from "./dictionary/add/mileage-dictionary-add-request.factory";
import MileageDictionaryAddRequest from "./dictionary/add/mileage-dictionary-add.request";
import MileageDictionaryAddResponse from "./dictionary/add/mileage-dictionary-add.response";
import MileageDictionaryDetailsRequest from "./dictionary/details/mileage-dictionary-details.request";
import MileageDictionaryDetailsResponse from "./dictionary/details/mileage-dictionary-details.response";
import mileageDictionaryListRequestFactory from "./dictionary/list/mileage-dictionary-list-request.factory";
import MileageDictionaryListRequest, {
  MileageDictionaryListRequestQueryParams,
} from "./dictionary/list/mileage-dictionary-list.request";
import MileageDictionaryListResponse from "./dictionary/list/mileage-dictionary-list.response";
import mileageDictionaryUpdateRequestFactory from "./dictionary/update/mileage-dictionary-update-request.factory";
import MileageDictionaryUpdateRequest, {
  MileageDictionaryUpdateRequestBody,
} from "./dictionary/update/mileage-dictionary-update.request";
import MileageDictionaryUpdateResponse from "./dictionary/update/mileage-dictionary-update.response";
import mileageAddressListRequestFactory from "./address/list/mileage-address-list-request.factory";
import MileageAddressListRequest, {
  MileageAddressListQueryParams,
} from "./address/list/mileage-address-list.request";
import MileageAddressListResponse from "./address/list/mileage-address-list.response";

const mileageApiService = () => {
  const getList = async (
    request: MileageListRequest,
    abortSignal: AbortSignal,
  ): Promise<MileageListResponse> => {
    const url = `/mileage`;

    const queryParams = mileageListRequestFactory.createQueryParams(request);

    return railyApiService().get({
      url,
      abortSignal,
      queryParams,
    });
  };

  const add = async (
    request: MileageAddRequest,
    abortSignal: AbortSignal,
  ): Promise<MileageAddResponse> => {
    const url = `/mileage`;

    const body: MileageAddRequestBody =
      mileageAddRequestFactory.createBody(request);

    return railyApiService().post({
      url,
      abortSignal,
      body,
    });
  };

  const download = async (
    request: MileageDownloadRequest,
    abortSignal: AbortSignal,
  ): Promise<MileageDownloadResponse> => {
    const url = `/mileage/download/csv`;

    const queryParams: MileageDownloadRequestQueryParams =
      mileageDownloadRequestFactory.createQueryParams(request);

    return railyApiService().getFile({
      url,
      abortSignal,
      queryParams,
    });
  };

  const downloadAllCSV = async (
    request: MileageDownloadAllCSVRequest,
    abortSignal: AbortSignal,
  ): Promise<MileageDownloadAllCSVResponse> => {
    const url = `/mileage/download/csv/all`;

    const queryParams: MileageDownloadAllCSVRequestQueryParams =
      mileageDownloadAllCSVRequestFactory.createQueryParams(request);

    return railyApiService().getFile({
      url,
      abortSignal,
      queryParams,
    });
  };

  const getDetails = async (
    request: MileageDetailsRequest,
    abortSignal: AbortSignal,
  ): Promise<MileageDetailsResponse> => {
    const url = `/mileage/${request.mileageUuid}`;

    return railyApiService().get({
      url,
      abortSignal,
    });
  };

  const updateMileage = async (
    request: MileageUpdateRequest,
    abortSignal: AbortSignal,
  ): Promise<HttpResponse<string>> => {
    const url = `/mileage/${request.mileageUuid}`;
    const body = mileageUpdateRequestFactory.createBody(request);

    return railyApiService().put({
      url,
      abortSignal,
      body,
    });
  };

  const addSettings = async (
    request: MileageSettingsAddRequest,
    abortSignal: AbortSignal,
  ): Promise<MileageSettingsAddResponse> => {
    const url = "/mileage/config";

    const body: MileageSettingsAddRequestBody =
      mileageSettingsAddRequestFactory.createBody(request);

    return railyApiService().post({
      url,
      abortSignal,
      body,
    });
  };

  const getSettingsDetails = async (
    request: MileageSettingsDetailsRequest,
    abortSignal: AbortSignal,
  ): Promise<MileageSettingsDetailsResponse> => {
    const url = `/mileage/config/${request.cargoCompanyUuid}`;

    const queryParams: MileageSettingsDetailsRequestQueryParams =
      mileageSettingsDetailsRequestFactory.createQueryParams(request);

    return railyApiService().get({
      url,
      abortSignal,
      queryParams,
    });
  };

  const updateSettings = async (
    request: MileageSettingsUpdateRequest,
    abortSignal: AbortSignal,
  ): Promise<MileageSettingsUpdateResponse> => {
    const url = `/mileage/config/${request.cargoCompanyUuid}`;

    const body: MileageSettingsUpdateRequestBody =
      mileageSettingsUpdateRequestFactory.createBody(request);

    return railyApiService().put({
      url,
      abortSignal,
      body,
    });
  };

  const addDictionary = async (
    request: MileageDictionaryAddRequest,
    abortSignal: AbortSignal,
  ): Promise<MileageDictionaryAddResponse> => {
    const url = `/mileage/dictionary/${request.cargoCompanyUuid}`;
    const body = mileageDictionaryAddRequestFactory.createBody(request);

    return railyApiService().post({
      url,
      abortSignal,
      body,
    });
  };

  const dictionaryList = async (
    request: MileageDictionaryListRequest,
    abortSignal: AbortSignal,
  ): Promise<MileageDictionaryListResponse> => {
    const url = `/mileage/dictionary/${request.cargoCompanyUuid}`;
    const queryParams: MileageDictionaryListRequestQueryParams =
      mileageDictionaryListRequestFactory.createQueryParams(request);

    return railyApiService().get({
      url,
      abortSignal,
      queryParams,
    });
  };

  const updateDictionary = async (
    request: MileageDictionaryUpdateRequest,
    abortSignal: AbortSignal,
  ): Promise<MileageDictionaryUpdateResponse> => {
    const url = `/mileage/dictionary/${request.cargoCompanyId}/record/${request.recordId}`;
    const body: MileageDictionaryUpdateRequestBody =
      mileageDictionaryUpdateRequestFactory.createBody(request);

    return railyApiService().put({
      url,
      abortSignal,
      body,
    });
  };

  const getDictionaryDetails = async (
    request: MileageDictionaryDetailsRequest,
    abortSignal: AbortSignal,
  ): Promise<MileageDictionaryDetailsResponse> => {
    const url = `/mileage/dictionary/${request.cargoCompanyUuid}/record/${request.recordUuid}`;

    return railyApiService().get({
      url,
      abortSignal,
    });
  };

  const getAddressList = async (
    request: MileageAddressListRequest,
    abortSignal: AbortSignal,
  ): Promise<MileageAddressListResponse> => {
    const url = `/mileage/address`;

    const queryParams: MileageAddressListQueryParams =
      mileageAddressListRequestFactory.createQueryParams(request);

    return railyApiService().get({
      url,
      abortSignal,
      queryParams,
    });
  };

  return {
    getList,
    add,
    download,
    getDetails,
    updateMileage,
    downloadAllCSV,
    addSettings,
    getSettingsDetails,
    updateSettings,
    addDictionary,
    dictionaryList,
    updateDictionary,
    getDictionaryDetails,
    getAddressList,
  };
};

export default mileageApiService;
